import {CommonModule} from '@angular/common';
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {MatButtonModule} from '@angular/material/button';
import {MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {TranslateModule} from '@ngx-translate/core';
import {forkJoin, of, Subscription} from 'rxjs';
import {catchError, map, switchMap, take} from 'rxjs/operators';
import {ActivitiesSearchEngineModule} from '@modules/activities/core/activities-search-engine';
import {ActivitiesSharedModule} from '@modules/activities/core/activities-shared.module';
import {ActivitySearchOptions} from '@modules/activities/core/activities-search-engine/activity-search-options';
import {GroupService} from '@modules/groups-management';
import {GroupManagementConfigurationService} from '@modules/groups-management/core/services/group-management-configuration.service';
import {
    SearchFilterDefinition,
    SearchFilterSelection,
    SearchFilterValue,
} from 'shared/search-engine';
import {MathiaSearchDataService} from '../mathia-search-data.service';
import {MathiaGenericSearchFilterComponent} from '../mathia-generic-search-filter/mathia-generic-search-filter.component';
import {
    buildMathiaActivitySearchInitialSelection,
    MathiaGroupLevelSource,
} from './mathia-activity-search-initial-selection';

@Component({
    selector: 'app-mathia-activity-search-dialog',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatProgressSpinnerModule,
        ActivitiesSearchEngineModule,
        ActivitiesSharedModule,
        MathiaGenericSearchFilterComponent,
        TranslateModule,
    ],
    templateUrl: './mathia-activity-search-dialog.component.html',
})
export class MathiaActivitySearchDialogComponent implements OnInit, OnDestroy {
    private readonly dialogRef = inject(MatDialogRef<MathiaActivitySearchDialogComponent>);
    private readonly searchData = inject(MathiaSearchDataService);
    private readonly groups = inject(GroupService);
    private readonly groupConfig = inject(GroupManagementConfigurationService);
    private initializationSubscription?: Subscription;

    public definitions: readonly SearchFilterDefinition<SearchFilterValue>[] = [];
    public selection: SearchFilterSelection<SearchFilterValue> = {};
    public activitySearchOptions: ActivitySearchOptions = {filters: {}};
    public isInitializing = true;
    private selectedActivityIds = new Set<string | number>();

    public ngOnInit(): void {
        const groups$ = this.groups.loadGroups().pipe(
            take(1),
            map(collection => collection.entities.map(entity => ({
                level: entity.get('level'),
                archived: entity.get('archived'),
                type: entity.get('type'),
            }) satisfies MathiaGroupLevelSource)),
            catchError(() => of([] as MathiaGroupLevelSource[])),
        );
        const definitions$ = this.searchData.getFilterDefinitions().pipe(
            take(1),
            catchError(() => of([] as SearchFilterDefinition<SearchFilterValue>[])),
        );

        this.initializationSubscription = forkJoin({groups: groups$, definitions: definitions$}).pipe(
            switchMap(({groups, definitions}) => {
                const selection = buildMathiaActivitySearchInitialSelection(
                    groups,
                    definitions,
                    this.groupConfig.settings.groupType,
                );

                if (!selection['level']) {
                    return of({definitions, selection});
                }

                return this.searchData.getFilterDefinitions(selection).pipe(
                    take(1),
                    map(filteredDefinitions => {
                        const filteredDefinitionByKey = new Map(
                            filteredDefinitions.map(definition => [definition.key, definition]),
                        );
                        const initializedDefinitions = definitions.map(definition => definition.key === 'level'
                            ? definition
                            : filteredDefinitionByKey.get(definition.key) ?? definition);

                        return {definitions: initializedDefinitions, selection};
                    }),
                    catchError(() => of({definitions, selection})),
                );
            }),
        ).subscribe(({definitions, selection}) => {
            this.definitions = definitions;
            this.selection = selection;
            this.activitySearchOptions = {
                filters: this.searchData.toBasicSearchFilters(selection),
            };
            this.isInitializing = false;
        });
    }

    public ngOnDestroy(): void {
        this.initializationSubscription?.unsubscribe();
    }

    public onSelectionChange(selection: SearchFilterSelection<SearchFilterValue>): void {
        this.selection = {...selection};
        this.selectedActivityIds.clear();
        this.activitySearchOptions = {
            filters: this.searchData.toBasicSearchFilters(this.selection),
        };
    }

    public toggleActivity(id: string | number): void {
        if (this.selectedActivityIds.has(id)) {
            this.selectedActivityIds.delete(id);
        } else {
            this.selectedActivityIds.add(id);
        }
    }

    public isSelected(id: string | number): boolean {
        return this.selectedActivityIds.has(id);
    }

    public get selectedActivityCount(): number {
        return this.selectedActivityIds.size;
    }

    public clearSelection(): void {
        this.selectedActivityIds.clear();
    }

    public close(): void {
        this.dialogRef.close([...this.selectedActivityIds]);
    }
}
